import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Reusable Pagination Component
 * Mobile: Shows Prev [current/total] Next
 * Desktop: Shows First Prev [1] [2] [3] ... [total] Next Last
 */
export const Pagination = ({ 
  pagination, 
  onPageChange, 
  itemLabel = 'item',
  showInfo = true 
}) => {
  const { page, per_page, total, total_pages, has_prev, has_next } = pagination;

  if (total_pages <= 1) {
    return (
      <div className="text-center text-sm text-slate-500 py-2 dark:text-zinc-400">
        Menampilkan {total} {itemLabel}
      </div>
    );
  }

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= total_pages) {
      onPageChange(newPage);
    }
  };

  // Generate page numbers for desktop
  const getPageNumbers = () => {
    const pages = [];
    const current = page;
    
    let start = Math.max(1, current - 2);
    let end = Math.min(total_pages, current + 2);
    
    if (current <= 3) end = Math.min(5, total_pages);
    if (current >= total_pages - 2) start = Math.max(1, total_pages - 4);
    
    // First page
    if (start > 1) {
      pages.push(
        <Button key={1} variant="outline" size="sm" onClick={() => goToPage(1)} className="w-9 h-9 p-0">
          1
        </Button>
      );
      if (start > 2) {
        pages.push(<span key="s-e" className="px-1 text-slate-500 text-sm dark:text-zinc-400">...</span>);
      }
    }
    
    // Page numbers
    for (let i = start; i <= end; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === current ? "default" : "outline"}
          size="sm"
          onClick={() => goToPage(i)}
          className={`w-9 h-9 p-0 ${i === current ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}`}
        >
          {i}
        </Button>
      );
    }
    
    // Last page
    if (end < total_pages) {
      if (end < total_pages - 1) {
        pages.push(<span key="e-e" className="px-1 text-slate-500 text-sm dark:text-zinc-400">...</span>);
      }
      pages.push(
        <Button key={total_pages} variant="outline" size="sm" onClick={() => goToPage(total_pages)} className="w-9 h-9 p-0">
          {total_pages}
        </Button>
      );
    }
    
    return pages;
  };

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl p-3 sm:p-4 border border-slate-200 dark:border-zinc-800">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Info Text */}
        {showInfo && (
          <div className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 text-center sm:text-left">
            <span className="hidden sm:inline">
              Menampilkan {((page - 1) * per_page) + 1} - {Math.min(page * per_page, total)} dari {total} {itemLabel}
            </span>
            <span className="sm:hidden">
              {total} {itemLabel}
            </span>
          </div>
        )}
        
        {/* Pagination Controls */}
        <div className="flex items-center gap-1">
          {/* First Page - Desktop only */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(1)}
            disabled={!has_prev}
            className="hidden sm:flex w-9 h-9 p-0"
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          
          {/* Previous */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(page - 1)}
            disabled={!has_prev}
            className="h-9 px-2 sm:px-3"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Prev</span>
          </Button>
          
          {/* Mobile: Current/Total indicator */}
          <div className="sm:hidden flex items-center px-3 h-9 bg-slate-100 dark:bg-zinc-800 rounded-lg text-sm font-medium">
            <span className="text-indigo-600 dark:text-indigo-400">{page}</span>
            <span className="text-slate-500 mx-1 dark:text-zinc-400">/</span>
            <span className="text-slate-600 dark:text-zinc-300">{total_pages}</span>
          </div>
          
          {/* Desktop: Page Numbers */}
          <div className="hidden sm:flex items-center gap-1">
            {getPageNumbers()}
          </div>
          
          {/* Next */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(page + 1)}
            disabled={!has_next}
            className="h-9 px-2 sm:px-3"
          >
            <span className="hidden sm:inline mr-1">Next</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
          
          {/* Last Page - Desktop only */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(total_pages)}
            disabled={!has_next}
            className="hidden sm:flex w-9 h-9 p-0"
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
